// One continuous square lattice: a flat playable surround rises into distant
// hills. The floor and slopes share geometry, UVs, spacing, motion and shading.
export function terrainHeight(x,z,bounds=[-25,-25,25,25]){
 const dx=Math.max(bounds[0]-x,0,x-bounds[2]),dz=Math.max(bounds[1]-z,0,z-bounds[3]);
 const distance=Math.hypot(dx,dz),t=Math.max(0,Math.min(1,(distance-10)/36)),rise=t*t*(3-2*t);
 return rise*Math.max(.5,7+4*Math.sin(x*.039+Math.sin(z*.023)*1.7)+3*Math.sin(z*.057+x*.017)+2*Math.cos(x*.081-z*.043));
}
export function createWireLandscape(T,scene,{floor=-.15,bounds=[-25,-25,25,25]}={}){
 const root=new T.Group();
 const geometry=new T.PlaneGeometry(384,384,192,192);geometry.rotateX(-Math.PI/2);
 // Keep two-meter samples nearby; stretch the outer ring to the horizon.
 const positions=geometry.attributes.position;
 const expand=v=>Math.sign(v)*(Math.abs(v)<=128?Math.abs(v):128+Math.pow((Math.abs(v)-128)/64,2)*1872);
 for(let i=0;i<positions.count;i++)positions.setXYZ(i,expand(positions.getX(i)),0,expand(positions.getZ(i)));
 const material=new T.ShaderMaterial({depthWrite:true,depthTest:true,side:T.DoubleSide,toneMapped:false,
  uniforms:{time:{value:0},echo:{value:0},floorY:{value:floor},levelBounds:{value:new T.Vector4(...bounds)}},
  vertexShader:`uniform float time,floorY;uniform vec4 levelBounds;varying vec3 worldPoint;
  void main(){
   vec2 xz=position.xz+floor(cameraPosition.xz/2.0)*2.0;
   vec2 outside=max(max(levelBounds.xy-xz,xz-levelBounds.zw),vec2(0.0));
   float rise=smoothstep(10.0,46.0,length(outside));
   vec2 terrain=xz-vec2(0.0,time*1.2);
   float h=7.0+4.0*sin(terrain.x*.039+sin(terrain.y*.023)*1.7)+3.0*sin(terrain.y*.057+terrain.x*.017)+2.0*cos(terrain.x*.081-terrain.y*.043);
   worldPoint=vec3(xz.x,floorY+rise*max(.5,h),xz.y);
   vec4 clip=projectionMatrix*viewMatrix*vec4(worldPoint,1.0);
   // Scenery reaches beyond gameplay far planes; retain near/portal clipping.
   gl_Position=vec4(clip.xy,min(clip.z,clip.w*.99999),clip.w);
  }`,
  fragmentShader:`uniform float time,echo;varying vec3 worldPoint;
  float hash(vec2 p){vec3 q=fract(vec3(p.xyx)*.1031);q+=dot(q,q.yzx+33.33);return fract((q.x+q.y)*q.z);}
  float lattice(vec2 p){
   vec2 width=max(fwidth(p),vec2(.0001));
   vec2 edge=abs(fract(p-.5)-.5)/width;
   // Remove only subpixel frequencies, not the entire distant landscape.
   vec2 lines=(1.0-min(edge,vec2(1.0)))*(1.0-smoothstep(vec2(.35),vec2(1.2),width));
   return max(lines.x,lines.y);
  }
  void main(){
   float distanceToEye=length(worldPoint-cameraPosition);
   float haze=smoothstep(100.0,1600.0,distanceToEye);
   vec2 cell=(worldPoint.xz-vec2(0.0,time*1.2))/2.0;
   float fine=lattice(cell),major=lattice(cell/5.0);
   float horizon=lattice(cell/25.0)*smoothstep(80.0,300.0,distanceToEye);
   vec3 normal=normalize(cross(dFdx(worldPoint),dFdy(worldPoint)));
   float slope=1.0-abs(normal.y);
   vec3 color=mix(vec3(.009,.002,.023),vec3(.025,.006,.048),slope);
   float line=max(max(fine*.48,major*.85),horizon*.7);
   color+=mix(vec3(.15,.029,.34),vec3(.3,.069,.51),major)*line*(1.0+echo*.15);
   float lane=floor(cell.x+.5),seed=hash(vec2(lane,71.3));
   float phase=fract((cell.y-time*(.8+seed*.45))/26.0+seed);
   float packet=exp(-abs(phase-.5)*110.0)*step(.82,seed);
   float xLine=1.0-min(abs(fract(cell.x-.5)-.5)/max(fwidth(cell.x),.0001),1.0);
   color+=mix(vec3(.025,.5,.65),vec3(.55,.045,.38),step(.5,seed))*packet*xLine*(.5+.5*sin(time*.27+seed*45.0))*.5;
   color=mix(color,vec3(.028,.006,.052)+vec3(.12,.025,.25)*line,haze*.55);
   gl_FragColor=vec4(color,1.0);
   #include <colorspace_fragment>
  }`,transparent:false});
 const surface=new T.Mesh(geometry,material);surface.frustumCulled=false;surface.renderOrder=-998;surface.onAfterRender=renderer=>renderer.clearDepth();root.add(surface);scene.add(root);
 return {root,update(time,echo=0){material.uniforms.time.value=time;material.uniforms.echo.value=echo;},dispose(){root.removeFromParent();geometry.dispose();material.dispose();}};
}
